/**
 * conflictMessageMixin
 *
 * Shared formatting for the "overlapping shift" validation errors returned by
 * POST/PUT /api/shifts. The backend returns structured conflict detail
 * (clientConflicts[] / conflictingShifts[] with clientName + effective times),
 * and this mixin turns that into a clear, human-readable message such as:
 *
 *   "Sky Smith already has a shift on Jun 27 from 2:30 PM - 4:00 PM."
 *
 * Self-contained (own time/date formatting) so it can be used by any
 * shift-creating page without depending on host-component formatters.
 */
export default {
  methods: {
    // Render a single ISO/datetime value as a 12-hour time, in the browser's
    // local timezone — consistent with how shift times are shown elsewhere.
    _conflictFormatTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    _conflictFormatTimeRange(start, end) {
      const s = this._conflictFormatTime(start);
      const e = this._conflictFormatTime(end);
      if (s && e) return `${s} - ${e}`;
      return s || e || "";
    },
    // Short, readable date for a conflict line, e.g. "Jun 27".
    formatConflictDate(value) {
      if (!value) return "this date";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "this date";
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    // Build one human-readable line for a single conflicting shift entry.
    formatConflictLine(entry) {
      if (!entry) return "";
      const start =
        entry.effective_start || entry.clock_in || entry.scheduled_start;
      const end = entry.effective_end || entry.clock_out || entry.scheduled_end;
      const dateLabel = this.formatConflictDate(start);
      const timeRange = this._conflictFormatTimeRange(start, end);
      const recurring = entry.isRecurringTemplate ? " (recurring)" : "";

      // Client conflicts carry the conflicting client's own name.
      if (entry.clientName) {
        const withCaregiver =
          entry.caregivers && entry.caregivers.length
            ? ` with ${entry.caregivers.join(", ")}`
            : "";
        return `${entry.clientName} already has a shift on ${dateLabel} from ${timeRange}${withCaregiver}${recurring}.`;
      }

      // Caregiver conflicts carry the clients booked on the conflicting shift.
      if (entry.clients && entry.clients.length) {
        return `Already booked on ${dateLabel} from ${timeRange} for ${entry.clients.join(", ")}${recurring}.`;
      }

      return `Conflicting shift on ${dateLabel} from ${timeRange}${recurring}.`;
    },
    // Assemble the full conflict message (headline + one line per conflicting
    // shift) from an API error payload. Returns { html, lineCount }.
    buildConflictMessage(data) {
      const headline = data.error || "Scheduling conflict detected.";
      const entries = [];
      if (Array.isArray(data.clientConflicts)) {
        for (const c of data.clientConflicts) {
          if (Array.isArray(c.conflictingShifts)) {
            entries.push(...c.conflictingShifts);
          }
        }
      }
      // Recurring-shift creation reports overlaps grouped by occurrence date.
      if (Array.isArray(data.conflictingInstances)) {
        for (const inst of data.conflictingInstances) {
          if (Array.isArray(inst.conflictingShifts)) {
            entries.push(...inst.conflictingShifts);
          }
        }
      }
      // Fall back to / also include the flattened list when present.
      if (!entries.length && Array.isArray(data.conflictingShifts)) {
        entries.push(...data.conflictingShifts);
      }
      const lines = entries
        .map((e) => this.formatConflictLine(e))
        .filter(Boolean);
      const html = lines.length
        ? `${headline}<br>${lines.join("<br>")}`
        : headline;
      return { html, lineCount: lines.length };
    },
    // Client-overlap conflicts are overridable: offer "Schedule anyway" and
    // return true when the scheduler confirms. The caller then retries the
    // save with acknowledgeClientOverlap: true (managers get a notification).
    // Returns false for non-overridable errors or when the user cancels.
    async confirmClientOverlap(error) {
      const data = error && error.response && error.response.data;
      if (!data || !data.clientOverlap) return false;
      const { html } = this.buildConflictMessage(data);
      try {
        await this.$confirm(
          `${html}<br><br>Schedule anyway? Managers will be notified of the overlap.`,
          "Client already scheduled",
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "Schedule Anyway",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
        return true;
      } catch {
        return false;
      }
    },
    // Caregiver availability conflicts are overridable, but the consequences
    // land in payroll and reports rather than on the schedule, so the confirm
    // spells out what the scheduler is signing up for. Returns true on confirm.
    async confirmCaregiverOverride(error) {
      const data = error && error.response && error.response.data;
      if (!data || !data.caregiverOverride) return false;

      const reason = data.error || "This caregiver is not available.";
      // Match the consequence to the kind of conflict the server reported.
      let consequence;
      if (Array.isArray(data.conflictingShifts) || data.conflictingInstances) {
        consequence =
          "Payroll counts the hours from both shifts, so this caregiver's total for the period will read as more hours than they can actually have worked — someone will have to correct it before payroll is approved.";
      } else if (data.timeOffRequests || data.conflictingDates) {
        consequence =
          "Their time-off request stays approved, so they may not show up. The shift would then need reassigning, or the time off revisiting with them.";
      } else {
        consequence =
          "The shift falls outside their work schedule, so it won't line up with their availability in reports and may push them into overtime the schedule was meant to avoid.";
      }

      try {
        await this.$confirm(
          `${reason}<br><br>${consequence}<br><br>Schedule anyway? Managers will be notified and the override is recorded on the shift.`,
          "Caregiver is not available",
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "Schedule Anyway",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
        return true;
      } catch {
        return false;
      }
    },
    // Central handler for a shift create/update error. Renders a rich, itemized
    // overlap message when conflict detail is present (toast for short lists,
    // modal for long ones); otherwise falls back to the generic message.
    // Returns true once handled.
    handleShiftSaveError(error, fallback = "Error saving shift") {
      const data = error && error.response && error.response.data;
      if (
        data &&
        (data.clientConflicts?.length ||
          data.conflictingShifts?.length ||
          data.conflictingInstances?.length)
      ) {
        const { html, lineCount } = this.buildConflictMessage(data);
        if (lineCount > 3) {
          // Long lists are easier to read (and don't auto-dismiss) in a modal.
          this.$alert(html, "Scheduling conflict", {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "OK",
          });
        } else {
          this.$notifyError(html);
        }
      } else if (data) {
        this.$notifyError(data.error || fallback);
      } else {
        this.$notifyError(`${fallback}: ${error}`);
      }
      return true;
    },
  },
};
